import React, { useState, useRef, useCallback } from 'react';
import { Button } from 'antd';
import { InputLabel, StyledQuillEditorModal } from './styles';
import TextBlockWithQuillRenderer from '../../containers/ComplexDocumentDesign/TextBlockWithQuillRenderer';
import { constructQuillJSON, constructAutoSaveJSON } from '../../utils/TextBlockHelper';
import { getCreativeBlockVariableContract } from '../../containers/ComplexDocumentDesign/complexDocumentHelper';
import { withTranslation } from 'react-i18next';
import { QUILL_EDITOR_MODAL as trans } from '../../components/Modals/translationValues';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../context/complexDocumentContext';

const NAMESPACE = ['QUILL_EDITOR_MODAL'];
const QuillEditorModal = props => {
  const {
    t,
    visible,
    value,
    isLoading = false,
    onSave,
    onCancel,
    characterLimit = 45,
    inputLabel = '',
  } = props;

  const customVarSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(customVarSelector, 'ComplexEmailSubjectEditor');

  const quillContentRef = useRef(value);

  const styleSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
  const [documentStyle] = useDocumentContext(styleSelector, 'ComplexEmailSubjectEditor');

  const [textEditable, setTextEditable] = useState(false);
  const [isCharLimitExceeded, setIsCharLimitExceeded] = useState(false);

  const updateChanges = useCallback(
    (contents, undoDiff, redoDiff, cb, ref, op = undefined, newGlobalVariables = []) => {
      // TODO : Save the subject contents with block variable contract
      const newContents = constructAutoSaveJSON(
        contents,
        documentStyle?.fontFamily,
        documentStyle?.fontSize,
        newGlobalVariables,
      );
      quillContentRef.current = {
        contents: newContents,
      };
      const variableContract = getCreativeBlockVariableContract(newContents, newGlobalVariables);
      quillContentRef.current.variableContract = variableContract.variables;
      cb();
    },
    [documentStyle],
  );

  const getEditorContentLength = useCallback(textLength => {
    if (textLength > characterLimit) {
      setIsCharLimitExceeded(true);
    } else {
      setIsCharLimitExceeded(false);
    }
  }, []);

  const dummyCallback = useCallback(() => {}, []);

  const defaultContent = constructQuillJSON(
    value,
    documentStyle?.fontFamily,
    documentStyle?.fontSize,
    globalVariables,
  );

  return (
    <StyledQuillEditorModal
      width="600px"
      centered
      maskClosable={false}
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          {t(trans.CANCEL_BUTTON_TEXT.key, trans.CANCEL_BUTTON_TEXT.value)}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isLoading}
          onClick={() => onSave(quillContentRef.current)}
          disabled={isCharLimitExceeded}
        >
          {isLoading
            ? t(trans.SAVING_BUTTON_TEXT.key, trans.SAVING_BUTTON_TEXT.value)
            : t(trans.SAVE_BUTTON_TEXT.key, trans.SAVE_BUTTON_TEXT.value)}
        </Button>,
      ]}
      keyboard={false}
    >
      <InputLabel>{inputLabel}</InputLabel>
      <div id="quill-modal-toolbar" style={{ visibility: 'hidden', height: 0 }} />
      <div id="quill-modal-variable" style={{ position: 'relative', top: 10 }} />
      <TextBlockWithQuillRenderer
        defaultContent={defaultContent}
        saveBlock={updateChanges}
        itemUUID={`quill-modal-editor`}
        selected={true}
        enableSideBar={dummyCallback}
        enableAI={false}
        toolbarInvisible={true}
        adjustScroll={dummyCallback}
        toolbarDOM={'quill-modal-toolbar'}
        variableDOM={'quill-modal-variable'}
        textEditable={textEditable}
        setTextEditable={setTextEditable}
        customClassName={'subject-editor'}
        getEditorContentLength={getEditorContentLength}
        isMailSubject={true}
      />
      {isCharLimitExceeded && (
        <p style={{ color: 'red', padding: '10px' }}>
          {t(trans.MAXIMUM_CHAR_LIMIT_ERROR.key, trans.MAXIMUM_CHAR_LIMIT_ERROR.value, {
            maxCharLength: characterLimit,
          })}
        </p>
      )}
    </StyledQuillEditorModal>
  );
};

export default withTranslation(NAMESPACE)(QuillEditorModal);
